import get from 'lodash/get'
import findLast from 'lodash/findLast'
import {
  DEFAULT_AI_USER,
  TRANSITIONS,
  CHARACTERS_SPECIFIC,
  EXCLUDE_FROM_TEXT_CONVERSION,
  EXCLUDE_CHARACTER_DEFINITION
} from './constants'

export const getBlockContent = block => {
  if (!block) return ''
  const elem = document.getElementById(block.id)
  return elem.innerText
}

export const removeNumbersFromString = (str) => str.replace(/\d+/g, '')

export const blocksToString = (blocks) => {
  return blocks.map(b => b.data.text).join('\n')
}

export const removeEmptyColons = text => {
  const regex = /([^<:^{]+|^):(?![^{]*})(?![^<]*>)/g
  return text.replace(regex, '$1')
}

export const isTransition = (text) => TRANSITIONS.includes(text.split(' ').filter(t => t).join(' ').toUpperCase())

export const isCharacter = (text) => /^[A-Z'. ]+$/i.test(text) && !EXCLUDE_CHARACTER_DEFINITION.includes(text.toLowerCase())

export const getCapitalLetters = (text) => {
  const regex = /\b[A-Z]{2,}(?:\s+[A-Z]{2,})*\b/g
  const matches = text.match(regex)
  if (matches) {
    return matches.join(' ')
  }
  return ''
}

export const isSceneHeading = (text) => (text.includes('EXT.') || text.includes('INT.')) // || !!SCENE_HEADING_SPECIFIC.find(s => text.startsWith(s))

export const isAct = (text) => text.startsWith('ACT')
export const isEpisode = (text) => text.startsWith('EPISODE') || text.startsWith('EP')

export const isParenthetical = (text) => {
  const match = text.match(/^\s*\(([^()]*)\)\s*$/)
  return match !== null && text === match[0] && text.toUpperCase() !== '(CONTINUED)'
}

export const removeParentheses = (str) => str.replace(/[()]/g, '')

export const findParentheses = (str) => {
  const matches = str.match(/\((.*?)\)/g)
  return matches || []
}

export const parseSymbols = (str = '') => {
  return [0, 1].reduce(value => {
    return value
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/g, '\'')
      .replace(/&apos;/gi, '\'')
      .replace(/&rsquo;/gi, '\'')
      .replace(/&lsquo;/gi, '\'')
      .replace(/&rdquo;/gi, '"')
      .replace(/&ldquo;/gi, '"')
      .replace(/&nbsp;/gi, ' ')
  }, str)
}

export const removeHtmlTags = (str = '') => {
  return parseSymbols(str || ''.toString()).replace(/<[^>]*>/g, '')
}

export const containCharacterSpecificText = (text) => findLast(CHARACTERS_SPECIFIC, s => text.indexOf(s) > -1)

const CHARACTER_SUFFIX_PATTERN = new RegExp(
  CHARACTERS_SPECIFIC
    .map(character => character.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|'),
  'gi'
)

const TRAILING_CHARACTER_DIRECTIVE_PATTERN = /(?:\s*\([^()]*\)\s*)+$/g

export const normalizeScreenplayText = (text = '') => {
  return removeHtmlTags(text)
    .replace(/[‘’]/g, '\'')
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
}

export const removeSpecificCharacters = (text) => {
  return normalizeScreenplayText(text)
    .replace(CHARACTER_SUFFIX_PATTERN, '')
    .replace(TRAILING_CHARACTER_DIRECTIVE_PATTERN, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export const normalizeScreenplayCharacterName = (text) => {
  return removeSpecificCharacters(text)
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim()
}

const ANY_PARENTHETICAL_PATTERN = /\([^()]*\)/g
const NUMBER_SUFFIX_PATTERN = /\s*#\s*(\d+)/g
const TRAILING_PUNCTUATION_PATTERN = /[.,:;\-\s]+$/

// Mirror of packages/shared/src/common/screenplay-character.js (studio cannot import @nolan/shared).
export const stripCastDirectives = (text = '') => {
  return normalizeScreenplayText(text)
    .replace(CHARACTER_SUFFIX_PATTERN, '')
    .replace(ANY_PARENTHETICAL_PATTERN, '')
    .replace(NUMBER_SUFFIX_PATTERN, ' #$1')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(TRAILING_PUNCTUATION_PATTERN, '')
}

export const normalizeCastName = (text = '') => stripCastDirectives(text).toUpperCase()

export const stringifyAllBlocks = blocks => {
  return blocks
    .filter(b => !EXCLUDE_FROM_TEXT_CONVERSION.includes(b.type))
    .map(block => {
      return get(block, 'data.text', '')
        .trim()
        .replace(/<[^>]*>?/gm, '')
    }).join('\n')
}

export const stringifySuggestionBlocks = blocks => {
  const array = []
  blocks
    .filter(b => !EXCLUDE_FROM_TEXT_CONVERSION.includes(b.type))
    .forEach(block => {
      // sometimes content is coming from ai unstructured and dialog, pharentetical, text at one
      // we need split them separately.
      const textArray = get(block, 'data.text', '').split('\n')
      textArray.forEach(text => {
        text.trim().replace(/<[^>]*>?/gm, '')
        array.push(text)
      })
    })
  return array.join('\n')
}

const genericTransform = (text) => {
  return text.split('\n').filter(t => t).reduce((acc, content) => {
    let [character, dialogue] = content.split(':')
    character = (character || '').replace(/"/g, '').trim()
    dialogue = (dialogue || '').replace(/"/g, '').trim()
    if (character && dialogue) {
      const characterData = {
        type: 'character',
        user: {},
        data: {
          text: character
        }
      }
      const dialogData = {
        type: 'dialogue',
        user: {},
        data: {
          text: dialogue
        }
      }
      acc.push(characterData)
      acc.push(dialogData)
    } else if (character && !dialogue && !isCharacter(character)) {
      const dialogData = {
        type: 'dialogue',
        user: {},
        data: {
          text: character
        }
      }
      acc.push(dialogData)
    } else if (character || dialogue) {
      const content = {
        type: isCharacter((character || dialogue)) ? 'character' : 'action',
        user: {},
        data: {
          text: (character || dialogue)
        }
      }
      acc.push(content)
    }
    return acc
  }, [])
}

export const describe = {
  toText: (data) => {
    const [type, text] = data[0].content.split('.')
    if (type === 'INT' || type === 'EXT') {
      const intExt = type === 'INT' ? 'interior' : 'exterior'
      return {
        type: intExt,
        text: (text || '').trim()
      }
    }
    return {}
  },
  toBlocks: (text) => {
    const content = {
      type: 'action',
      user: DEFAULT_AI_USER,
      data: {
        text: text
      }
    }
    return [content]
  }
}

export const complete = {
  toText: (data) => {
    const excludingTypes = ['transition', 'act', 'episode']
    const content = data.filter(d => !excludingTypes.includes(d.type))
    return content.reduce((acc, val, key) => {
      const current = val
      if (current) {
        if (current.type === 'character') {
          let characterText = `${current.content}: `
          for (let i = (key + 1); i <= data.length; i++) {
            const next = data[i]
            if (next) {
              if (next.type === 'character') {
                break
              }
              if (next.type === 'dialogue' || next.type === 'parenthetical') {
                characterText = `${characterText} ${next.content}`
                data.splice(i, 1)
              }
            }
          }
          acc += `${characterText}\n`
        } else {
          acc += `${current.content}\n`
        }
      }
      return acc
    }, '')
  },
  toBlocks: (text) => {
    return text.split('\n').filter(t => t).reduce((acc, content) => {
      const [character, dialogue] = content.split(':')
      if (character && dialogue) {
        const characterData = {
          type: 'character',
          user: DEFAULT_AI_USER,
          data: {
            text: character
          }
        }
        const dialogData = {
          type: 'dialogue',
          user: DEFAULT_AI_USER,
          data: {
            text: dialogue
          }
        }
        acc.push(characterData)
        acc.push(dialogData)
      } else if (character || dialogue) {
        const content = {
          type: 'action',
          user: DEFAULT_AI_USER,
          data: {
            text: character || dialogue
          }
        }
        acc.push(content)
      }
      return acc
    }, [])
  }
}

export const conversation = {
  toText: (data) => {
    return data.reduce((acc, val, key) => {
      const current = val
      // const next = data[key + 1]
      if (current && current.type === 'character') {
        let characterText = `${current.content}: `
        for (let i = (key + 1); i <= data.length; i++) {
          const next = data[i]
          if (next) {
            if (next.type === 'character') {
              break
            }
            characterText = `${characterText} ${next.content}.`
          }
        }
        acc += `${characterText}\n`
      }
      return acc
    }, '')
  },
  toBlocks: (text) => genericTransform(text)
}

export const arrayIsDialog = (array) => {
  let groups = []
  let currentGroup = null

  for (let i = 0; i < array.length; i++) {
    const item = array[i]

    if (item.type === 'character') {
      // start a new group with the character
      currentGroup = [item]
      groups.push(currentGroup)
    } else if (
      (item.type === 'dialogue' || item.type === 'parenthetical') &&
      currentGroup
    ) {
      // add dialogue or parenthetical to current group
      currentGroup.push(item)
    }
  }

  // filter out groups that don't meet the criteria
  groups = groups.filter((group) => {
    const hasCharacter = group.some((item) => item.type === 'character')
    const hasDialogue = group.some((item) => item.type === 'dialogue')
    const hasParenthetical = group.some(
      (item) => item.type === 'parenthetical'
    )
    return (
      hasCharacter &&
      (hasDialogue || hasParenthetical) &&
      (hasDialogue && (hasParenthetical || hasDialogue || hasParenthetical))
    )
  })

  return {
    groups,
    isDialogue: groups.length >= 2
  }
}

// nlp(`What a totally amazing, excellent discovery - (not!)`).parentheses().json({normal:true})[0]
